/**
 * M21 — contrato único de listagem de procedimentos, catálogos e consumíveis globais.
 * Cursor é um ID opaco ao usuário; não carrega contexto de Cliente.
 */
export interface NdtGlobalListQuery {
  search?: string;
  status?: string;
  category?: string;
  code?: string;
  title?: string;
  referenceStandard?: string;
  cursor?: string | null;
  limit?: number;
}
export interface NdtGlobalListResult<T> {
  items: T[];
  total: number;
  nextCursor: string | null;
}
export function sanitizeNdtGlobalQuery(query: NdtGlobalListQuery = {}): NdtGlobalListQuery & {limit:number} {
  const limit = Number(query.limit ?? 25);
  if (!Number.isInteger(limit) || limit < 1) throw new RangeError("Limite de página inválido.");
  const cursor = String(query.cursor ?? "").trim();
  if (cursor.length > 256) throw new RangeError("Cursor inválido.");
  return { limit: Math.min(limit, 100), search: String(query.search ?? "").trim().slice(0, 120),
    status: String(query.status ?? "").trim().slice(0, 48),
    category: String(query.category ?? "").trim().slice(0, 100),
    code: String(query.code ?? "").trim().slice(0, 100),
    title: String(query.title ?? "").trim().slice(0, 100),
    referenceStandard: String(query.referenceStandard ?? "").trim().slice(0, 100), cursor };
}
export function paginateNdtGlobalItems<T extends {id:string}>(items: readonly T[], input: NdtGlobalListQuery): NdtGlobalListResult<T> {
  const q = sanitizeNdtGlobalQuery(input);
  const needle = q.search?.normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase();
  const found = items.filter(item => {
    const obj = item as Record<string, unknown>;
    if (q.status && obj["status"] !== q.status) return false;
    if (q.category && ![obj["type"],obj["method"],obj["catalogKey"]].includes(q.category)) return false;
    for (const field of ["code", "title", "referenceStandard"] as const) {
      const requested = q[field]?.toLocaleLowerCase("pt-BR");
      if (requested && !String(obj[field] ?? "").toLocaleLowerCase("pt-BR").includes(requested)) return false;
    }
    const haystack = [obj["code"],obj["name"],obj["title"],obj["brand"],obj["product"],obj["lot"],obj["referenceStandard"]]
      .filter(Boolean).join(" ").normalize("NFD").replace(/[\u0300-\u036f]/g,"").toLowerCase();
    return !needle || haystack.includes(needle);
  }).sort((a,b)=>a.id.localeCompare(b.id));
  const first = q.cursor ? found.findIndex(item => item.id > q.cursor!) : 0;
  const offset = first === -1 ? found.length : first;
  const page = found.slice(offset, offset + q.limit);
  return {items:page, total:found.length, nextCursor:offset+page.length<found.length?page[page.length-1]?.id ?? null:null};
}
